import React, { useState, useMemo, useEffect, useCallback } from "react";
import TicketLogHeader from "./TicketLogHeader";
import { TicketCardView } from "../TicketCardView";
import { TicketLogGroupByView } from "../TicketLogGroupByView";
import { useCoreContext } from "../../../../context/global/CoreContext";
import { Search, Filter } from "../../../../components/icons/svg/outline";
import { ConfirmModal } from "../../../../components/ui";
import { itemMatchesSearchQuery } from "../../../../utils/common/searchFilter";

/**
 * List/Card/Group view toggle icons
 */
const ListIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const GridIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const GroupIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

export const DEFAULT_TICKET_PAGINATION_THRESHOLD = 50;
export const DEFAULT_TICKET_PAGE_SIZE_STEP = 50;
const PAGE_SIZE_ALL = "all";

/**
 * Build page-size options from the current ticket count.
 *
 * @param {number} itemCount - Number of filtered/sorted tickets.
 * @param {number} pageSizeStep - Increment between page-size options.
 * @param {Array<number|string>|null} pageSizeOptions - Optional explicit options.
 * @returns {Array<number|string>}
 */
function getPageSizeOptions(itemCount, pageSizeStep, pageSizeOptions = null) {
  if (Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0) {
    return [...pageSizeOptions, PAGE_SIZE_ALL]
      .filter((option) => option === PAGE_SIZE_ALL || Number(option) < itemCount)
      .filter((option, index, options) => options.indexOf(option) === index);
  }

  const options = [];
  for (let size = pageSizeStep; size < itemCount; size += pageSizeStep) {
    options.push(size);
  }
  return [...options, PAGE_SIZE_ALL];
}

/**
 * Return a valid page size value for the current table state.
 *
 * @param {number|string|undefined} defaultPageSize - Requested default page size.
 * @param {number} fallbackPageSize - Fallback numeric page size.
 * @returns {number|string}
 */
function getInitialPageSize(defaultPageSize, fallbackPageSize) {
  return defaultPageSize || fallbackPageSize;
}

/**
 * TicketLogTable - Configurable ticket log table component
 * 
 * A reusable table component for ticket-type modules (T&M, RFI, etc.)
 * Provides search, filtering, sorting, and configurable columns.
 * Supports both list (table) and card view modes.
 * 
 * @example
 * // Basic usage with column config
 * <TicketLogTable
 *   data={tickets}
 *   columns={[
 *     { key: 'ticket_id', label: 'ID', sortable: true },
 *     { key: 'description', label: 'Description', sortable: true },
 *   ]}
 *   RowComponent={MyTicketRow}
 *   CardComponent={MyTicketCard}
 *   onRowClick={(ticket) => navigate(`/ticket/${ticket.id}`)}
 * />
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of ticket objects to display
 * @param {Array} props.columns - Column configuration array
 * @param {React.Component} props.RowComponent - Component to render each table row
 * @param {React.Component} props.CardComponent - Component to render each card (optional)
 * @param {Function} props.onRowClick - Callback when row/card is clicked
 * @param {Array} props.searchFields - Fields to search (default: ['description'])
 * @param {Array} props.statusFilters - Custom status filter options
 * @param {Function} props.statusFilterFn - Custom filter function for status
 * @param {Function} props.onFilteredCountChange - Callback with filtered count
 * @param {Function} props.onFilteredTotalChange - Callback with filtered total
 * @param {Function} props.calculateTotal - Function to calculate total from filtered items
 * @param {string} props.emptyMessage - Message when no items found
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {Object} props.defaultSort - Default sort { field: string, direction: 'asc'|'desc' }
 * @param {string} props.defaultView - Default view mode ('list', 'card', or 'group')
 * @param {boolean} props.showViewToggle - Whether to show view toggle (default: true if CardComponent provided)
 * @param {boolean} [props.showToolbar=true] - Whether to show the search/filter/view toolbar.
 * @param {boolean} [props.showSearch=true] - Whether to show the search input.
 * @param {boolean} [props.showJobFilter=true] - Whether to show the job filter dropdown.
 * @param {boolean} [props.showStatusFilter=true] - Whether to show the status filter dropdown.
 * @param {string} props.groupBy - Field key to group by when in 'group' view (e.g., 'job_number', 'status')
 * @param {string} [props.groupSortDirection='desc'] - Sort order for groups in group view: 'asc' or 'desc'. Default 'desc' so higher job numbers appear first.
 * @param {Function} props.getGroupLabel - Optional function to format group label: (groupValue, items) => string
 * @param {Function} props.getGroupValue - Optional function to extract group value from item: (item) => any
 * @param {number} [props.paginationThreshold=50] - Show pagination once filtered item count exceeds this amount.
 * @param {number} [props.pageSizeStep=50] - Increment used to generate page-size options.
 * @param {number|string} [props.defaultPageSize] - Initial page size; use "all" to disable paging by default.
 * @param {Array<number|string>} [props.pageSizeOptions] - Optional explicit page-size options.
 * @param {Function} [props.onPaginationChange] - Callback with current pagination state and actions.
 * @param {number|string} [props.highlightAddedItemId] - Optional ID of item to briefly highlight (e.g. after clone)
 * @param {number|string} [props.highlightDeletedItemId] - Optional ID of item to show red delete flash
 * @param {Function} [props.onDelete] - (item) => Promise - API delete; when provided with executeDelete, triggers confirm modal
 * @param {Function} [props.executeDelete] - (item, onDelete) => Promise - Context method: red flash, delete, silent refresh
 */
const TicketLogTable = ({
  data = [],
  columns = [],
  RowComponent,
  CardComponent,
  onRowClick,
  searchFields = ["description"],
  statusFilters = [],
  statusFilterFn,
  onFilteredCountChange,
  onFilteredTotalChange,
  calculateTotal,
  emptyMessage = "No items found",
  searchPlaceholder = "Search...",
  defaultSort = { field: "updated_at", direction: "desc" },
  defaultView = "list",
  showViewToggle,
  showToolbar = true,
  showSearch = true,
  showJobFilter = true,
  showStatusFilter = true,
  // Group by view props
  groupBy,
  groupSortDirection = "desc",
  getGroupLabel,
  getGroupValue,
  paginationThreshold = DEFAULT_TICKET_PAGINATION_THRESHOLD,
  pageSizeStep = DEFAULT_TICKET_PAGE_SIZE_STEP,
  defaultPageSize,
  pageSizeOptions,
  onPaginationChange,
  // Pass-through props for RowComponent/CardComponent
  rowProps = {},
  cardProps = {},
  highlightAddedItemId,
  highlightDeletedItemId,
  onDelete,
  executeDelete,
}) => {
  const { allJobsList } = useCoreContext();
  
  // Delete confirmation: when user clicks Delete we show modal; on confirm close modal immediately then run executeDelete
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDeleteItem || !onDelete || !executeDelete) return;
    const itemToDelete = pendingDeleteItem;
    setPendingDeleteItem(null);
    try {
      await executeDelete(itemToDelete, onDelete);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  }, [pendingDeleteItem, onDelete, executeDelete]);

  const handleDeleteClose = useCallback(() => setPendingDeleteItem(null), []);

  // When delete is enabled (onDelete + executeDelete), row/card get onDeleteRequest to open modal; else onDelete for direct call
  const deleteProp = useMemo(() => {
    if (onDelete && executeDelete) {
      return { onDeleteRequest: (item) => setPendingDeleteItem(item) };
    }
    if (onDelete) {
      return { onDelete };
    }
    return {};
  }, [onDelete, executeDelete]);
  
  // View mode state
  const [viewMode, setViewMode] = useState(defaultView);
  
  // Sorting state
  const [sortField, setSortField] = useState(defaultSort.field);
  const [sortDirection, setSortDirection] = useState(defaultSort.direction);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedJob, setSelectedJob] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    getInitialPageSize(defaultPageSize, PAGE_SIZE_ALL)
  );

  // Determine if view toggle should be shown
  const shouldShowViewToggle = showViewToggle ?? !!CardComponent;
  const shouldShowToolbar =
    showToolbar &&
    (showSearch ||
      showJobFilter ||
      (showStatusFilter && statusFilters.length > 0) ||
      shouldShowViewToggle);

  // Handle column sort click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get unique jobs from data
  const jobsInData = useMemo(() => {
    const uniqueJobs = new Set();
    data.forEach((item) => {
      if (item.job) {
        uniqueJobs.add(item.job);
      }
    });
    return Array.from(uniqueJobs)
      .map((jobId) => allJobsList.find((job) => job.id === jobId))
      .filter(Boolean)
      .sort((a, b) => a.number - b.number);
  }, [data, allJobsList]);

  // Filter data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // Job filter
      if (selectedJob !== "all" && item.job !== parseInt(selectedJob)) {
        return false;
      }

      // Status filter (custom function if provided)
      if (selectedStatus !== "all" && statusFilterFn) {
        if (!statusFilterFn(item, selectedStatus)) {
          return false;
        }
      }

      if (!itemMatchesSearchQuery(item, searchQuery, searchFields)) {
        return false;
      }

      return true;
    });
  }, [data, selectedJob, selectedStatus, searchQuery, searchFields, statusFilterFn]);

  // Calculate filtered total (if calculateTotal provided)
  const filteredTotal = useMemo(() => {
    if (calculateTotal) {
      return calculateTotal(filteredData);
    }
    return 0;
  }, [filteredData, calculateTotal]);

  // Notify parent of filtered count
  useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(filteredData.length);
    }
  }, [filteredData.length, onFilteredCountChange]);

  // Notify parent of filtered total
  useEffect(() => {
    if (onFilteredTotalChange) {
      onFilteredTotalChange(filteredTotal);
    }
  }, [filteredTotal, onFilteredTotalChange]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;

    const sortableColumn = columns.find((col) => col.key === sortField);
    
    return [...filteredData].sort((a, b) => {
      let aValue, bValue;

      // Use custom sort value getter if provided
      if (sortableColumn?.getSortValue) {
        aValue = sortableColumn.getSortValue(a);
        bValue = sortableColumn.getSortValue(b);
      } else {
        // Default: get value by key (supports nested keys like 'pricing.total_cost')
        aValue = sortField.split('.').reduce((obj, key) => obj?.[key], a) ?? "";
        bValue = sortField.split('.').reduce((obj, key) => obj?.[key], b) ?? "";
      }

      // Handle string comparison
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = (bValue || "").toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortField, sortDirection, columns]);

  const shouldPaginate = sortedData.length > paginationThreshold;
  const generatedPageSizeOptions = useMemo(
    () => getPageSizeOptions(sortedData.length, pageSizeStep, pageSizeOptions),
    [sortedData.length, pageSizeStep, pageSizeOptions]
  );
  const numericPageSize =
    pageSize === PAGE_SIZE_ALL ? sortedData.length || 1 : Number(pageSize);
  const visiblePageSizeOptions = useMemo(() => {
    if (pageSize === PAGE_SIZE_ALL || generatedPageSizeOptions.includes(numericPageSize)) {
      return generatedPageSizeOptions;
    }

    const numericOptions = generatedPageSizeOptions.filter(
      (option) => option !== PAGE_SIZE_ALL
    );
    return [
      ...numericOptions,
      numericPageSize,
    ]
      .filter((option, index, options) => options.indexOf(option) === index)
      .sort((a, b) => a - b)
      .concat(PAGE_SIZE_ALL);
  }, [generatedPageSizeOptions, numericPageSize, pageSize]);
  const totalPages = shouldPaginate
    ? Math.max(1, Math.ceil(sortedData.length / numericPageSize))
    : 1;
  const paginatedData = useMemo(() => {
    if (!shouldPaginate || pageSize === PAGE_SIZE_ALL) {
      return sortedData;
    }

    const startIndex = (currentPage - 1) * numericPageSize;
    return sortedData.slice(startIndex, startIndex + numericPageSize);
  }, [currentPage, numericPageSize, pageSize, shouldPaginate, sortedData]);
  const pageStart = shouldPaginate && sortedData.length
    ? (currentPage - 1) * numericPageSize + 1
    : 0;
  const pageEnd = shouldPaginate
    ? Math.min(currentPage * numericPageSize, sortedData.length)
    : sortedData.length;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedJob, selectedStatus, sortField, sortDirection, pageSize, viewMode]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((page) => Math.max(1, page - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((page) => Math.min(totalPages, page + 1));
  }, [totalPages]);

  const handlePageSizeChange = useCallback((value) => {
    setPageSize(value === PAGE_SIZE_ALL ? PAGE_SIZE_ALL : Number(value));
  }, []);

  const paginationState = useMemo(
    () => ({
      shouldPaginate,
      currentPage,
      totalPages,
      pageStart,
      pageEnd,
      totalItems: sortedData.length,
      canGoPrevious: currentPage > 1,
      canGoNext: currentPage < totalPages,
      pageSize,
      pageSizeOptions: visiblePageSizeOptions,
      onPreviousPage: handlePreviousPage,
      onNextPage: handleNextPage,
      onPageSizeChange: handlePageSizeChange,
    }),
    [
      shouldPaginate,
      currentPage,
      totalPages,
      pageStart,
      pageEnd,
      sortedData.length,
      pageSize,
      visiblePageSizeOptions,
      handlePreviousPage,
      handleNextPage,
      handlePageSizeChange,
    ]
  );

  useEffect(() => {
    onPaginationChange?.(paginationState);
  }, [onPaginationChange, paginationState]);

  // Get visible columns (for header)
  const visibleColumns = columns.filter((col) => !col.hidden);

  const mergedRowProps = useMemo(
    () => ({ ...rowProps, ...deleteProp, highlightAddedItemId, highlightDeletedItemId }),
    [rowProps, deleteProp, highlightAddedItemId, highlightDeletedItemId]
  );
  const mergedCardProps = useMemo(
    () => ({ ...cardProps, ...deleteProp, highlightAddedItemId, highlightDeletedItemId }),
    [cardProps, deleteProp, highlightAddedItemId, highlightDeletedItemId]
  );

  return (
    <div>
      {/* Delete confirmation modal (base ticket system) */}
      <ConfirmModal
        isOpen={!!pendingDeleteItem}
        title="Delete ticket?"
        text="Are you sure you want to delete this item? This action cannot be undone."
        buttonText="Delete"
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteClose}
      />

      {shouldShowToolbar ? (
        <div className="p-2 md:p-4 bg-white border-b border-gray-200 flex flex-col md:flex-row gap-4">
          {/* Search Input - Full width on mobile, flex-1 on desktop */}
          {showSearch ? (
            <div className="relative w-full md:flex-1 order-1 md:order-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="block caret-black w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          ) : null}

          {/* Second Row Container (Mobile) - Job Filter + Status Filter + View Toggle */}
          <div className="flex flex-row flex-wrap gap-2 order-2 md:contents">
            {/* Job Filter Dropdown */}
            {showJobFilter ? (
              <div className="relative w-1/3 md:w-64 md:flex-none order-1 md:order-2">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Filter className="h-5 w-5 text-gray-700" />
                </div>
                <select
                  value={selectedJob}
                  onChange={(e) => setSelectedJob(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: "right 0.5rem center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "1.5em 1.5em",
                  }}
                >
                  <option value="all">All Jobs</option>
                  {jobsInData.map((job) => (
                    <option key={job.id} value={job.id}>
                      Job {job.number}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {/* Status Filter Dropdown (if statusFilters provided) */}
            {showStatusFilter && statusFilters.length > 0 && (
              <div className="relative w-1/3 md:w-64 md:flex-none order-2 md:order-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Filter className="h-5 w-5 text-gray-700" />
                </div>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: "right 0.5rem center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "1.5em 1.5em",
                  }}
                >
                  <option value="all">All Statuses</option>
                  {statusFilters.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* View Toggle */}
            {shouldShowViewToggle && (
              <div className="flex items-center border border-gray-300 rounded-md overflow-hidden flex-shrink-0 w-1/3 md:w-auto order-3 md:order-4">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-1.5 sm:p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${
                    viewMode === "list"
                      ? "bg-blue-500 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                  title="List view"
                >
                  <ListIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={() => setViewMode("card")}
                  className={`p-1.5 sm:p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${
                    viewMode === "card"
                      ? "bg-blue-500 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                  title="Card view"
                >
                  <GridIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                {groupBy && (
                  <button
                    onClick={() => setViewMode("group")}
                    className={`p-1.5 sm:p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${
                      viewMode === "group"
                        ? "bg-blue-500 text-white"
                        : "bg-white text-gray-600 hover:bg-gray-100"
                    }`}
                    title="Group by view"
                  >
                    <GroupIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Content - List, Card, or Group View */}
      {viewMode === "card" && CardComponent ? (
        <TicketCardView
          data={paginatedData}
          columns={visibleColumns}
          CardComponent={CardComponent}
          onCardClick={onRowClick}
          emptyMessage={emptyMessage}
          cardProps={mergedCardProps}
        />
      ) : viewMode === "group" && groupBy ? (
        <TicketLogGroupByView
          data={paginatedData}
          columns={visibleColumns}
          RowComponent={RowComponent}
          onRowClick={onRowClick}
          groupBy={groupBy}
          groupSortDirection={groupSortDirection}
          getGroupLabel={getGroupLabel}
          getGroupValue={getGroupValue}
          emptyMessage={emptyMessage}
          rowProps={mergedRowProps}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <TicketLogHeader
              columns={visibleColumns}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    <i>{emptyMessage}</i>
                  </td>
                </tr>
              ) : (
                paginatedData.map((item) => (
                  <RowComponent
                    key={item.id}
                    item={item}
                    columns={visibleColumns}
                    onRowClick={onRowClick}
                    {...mergedRowProps}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default TicketLogTable;
